import * as React from 'react'
import { useTranslation } from 'react-i18next'

import { htmlToPlainText } from '#/shared/lib/html'
import {
  sanitizeSmsFragment,
  sanitizeSmsHtml,
  sanitizeSmsText,
} from '#/shared/lib/sms-sanitize'
import { cn } from '#/shared/lib/utils'
import { Button } from '#/shared/ui/button'
import { Checkbox } from '#/shared/ui/checkbox'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '#/shared/ui/dropdown-menu'
import { AttachIcon, ChevronDownIcon, SendIcon } from '#/shared/ui/icons'
import { NoteEditor } from '#/shared/ui/note-editor'
import type {
  NoteEditorController,
  SlashCommandGroup,
  SlashCommandItem,
} from '#/shared/ui/note-editor'
import { RecipientsField } from '#/shared/ui/recipients-field'
import type { Recipient } from '#/shared/ui/recipients-field'
import { Select } from '#/shared/ui/select'
import type { SelectOption } from '#/shared/ui/select'
import { Skeleton } from '#/shared/ui/skeleton'
import { SmsSegmentCounter } from '#/shared/ui/sms-segment-counter'
import { TemplatePicker } from '#/shared/ui/template-picker'

interface InsertFieldMenuOption {
  id: string
  label: string
  suffix?: string
  insert: string
}

interface InsertFieldMenuGroup {
  label: string
  items: InsertFieldMenuOption[]
  subgroups: InsertFieldMenuGroup[]
}

interface Attachment {
  id: string
  name: string
  size: number
  url?: string
  selected?: boolean
}

interface FromOption {
  value: string
  label: string
}

interface PhoneEditorProps {
  from?: string
  fromOptions?: (string | FromOption)[]
  onFromChange?: (value: string) => void
  recipients?: Recipient[]
  defaultRecipients?: Recipient[]
  onRemoveRecipient?: (id: string) => void
  onAddRecipient?: (value: string) => void
  validateRecipient?: (value: string) => boolean
  templateOptions?: SelectOption[]
  templateLoading?: boolean
  onTemplateSelect?: (value: string) => void
  insertFieldOptions?: string[]
  onInsertFieldSelect?: (value: string) => void
  insertFieldGroups?: InsertFieldMenuGroup[]
  onInsertFieldOption?: (option: InsertFieldMenuOption) => void
  /** Grouped fields for the editor's `/` quick-insert menu (replaces the
   *  insert-field dropdown when provided). */
  slashGroups?: SlashCommandGroup[]
  onSlashInsert?: (item: SlashCommandItem) => void
  /** Deliverability summary rendered under the recipient rows (e.g. the
   *  "Sending to X of Y selected" bar). */
  recipientsSummary?: React.ReactNode
  enableFrom?: boolean
  enableCc?: boolean
  enableBcc?: boolean
  onCcChange?: (values: string[]) => void
  onBccChange?: (values: string[]) => void
  enableSubject?: boolean
  enableTemplate?: boolean
  enableInsertField?: boolean
  enableAttachments?: boolean
  enableUpload?: boolean
  subject?: string
  defaultSubject?: string
  onSubjectChange?: (value: string) => void
  subjectPlaceholder?: string
  onUpload?: (files: FileList) => void
  uploadAccept?: string
  uploadMultiple?: boolean
  attachments?: Attachment[]
  attachmentsLabel?: string
  attachmentsLoading?: boolean
  attachmentsSelectable?: boolean
  attachmentsScrollable?: boolean
  onViewAttachment?: (attachment: Attachment) => void
  onToggleAttachment?: (id: string, selected: boolean) => void
  value?: string
  onChange?: (html: string) => void
  onCancel?: () => void
  onSave?: (html: string) => void
  saveLabel?: string
  placeholder?: string
  enableTable?: boolean
  enableImage?: boolean
  enableLink?: boolean
  enableTextAlign?: boolean
  enableSource?: boolean
  /** Preserve `style` attributes and `<div>` containers (designed email
   *  templates) through the editor round-trip. */
  enableHtmlFidelity?: boolean
  /** Show the floating mic that dictates speech into the body at the cursor. */
  enableVoice?: boolean
  controllerRef?: React.Ref<NoteEditorController>
  compact?: boolean
  /** Show the live SMS character + segment count in the footer (SMS channel). */
  showSmsSegments?: boolean
}

function useFormatSize() {
  const { t } = useTranslation()
  return React.useCallback(
    (bytes: number) => {
      if (bytes < 1024) return t('editor.size.b', { value: bytes })
      if (bytes < 1024 * 1024)
        return t('editor.size.kb', { value: Math.round(bytes / 1024) })
      return t('editor.size.mb', {
        value: (bytes / (1024 * 1024)).toFixed(1),
      })
    },
    [t],
  )
}

function AttachmentRow({
  attachment,
  selectable,
  onView,
  onToggleSelect,
}: {
  attachment: Attachment
  selectable?: boolean
  onView?: (a: Attachment) => void
  onToggleSelect?: (id: string, selected: boolean) => void
}) {
  const { t } = useTranslation()
  const formatSize = useFormatSize()
  const a = attachment
  return (
    <div className="flex h-7 w-full items-center justify-between">
      <div className="flex h-full items-center gap-2.5 px-1">
        {selectable ? (
          <Checkbox
            checked={!!a.selected}
            onCheckedChange={(c) => onToggleSelect?.(a.id, c === true)}
            aria-label={t('editor.attachments.selectAria', { name: a.name })}
          />
        ) : (
          <AttachIcon className="size-5 text-gray-500" />
        )}
        <span
          className={cn(
            'text-sm',
            selectable && !a.selected ? 'text-gray-500' : 'text-gray-900',
          )}
        >
          {a.name}
        </span>
      </div>
      <div className="flex h-full items-center gap-2.5">
        <span className="text-xs text-gray-500">{formatSize(a.size)}</span>
        <Button
          variant="outline"
          size="xs"
          pressEffect
          onClick={() => onView?.(a)}
          asChild={!!a.url && !onView}
        >
          {a.url && !onView ? (
            <a href={a.url} target="_blank" rel="noreferrer">
              {t('editor.attachments.view')}
            </a>
          ) : (
            <span>{t('editor.attachments.view')}</span>
          )}
        </Button>
      </div>
    </div>
  )
}

function AttachmentRowSkeleton() {
  return (
    <div className="flex h-7 w-full items-center justify-between">
      <div className="flex h-full items-center gap-2.5 px-1">
        <Skeleton className="size-4 shrink-0 rounded-sm" />
        <Skeleton className="h-4 w-40" />
      </div>
      <div className="flex h-full items-center gap-2.5">
        <Skeleton className="h-3 w-12" />
        <Skeleton className="h-7 w-12 rounded-md" />
      </div>
    </div>
  )
}

function AttachmentsList({
  label,
  attachments,
  loading,
  selectable,
  scrollable,
  onView,
  onToggleSelect,
}: {
  label: string
  attachments: Attachment[]
  loading?: boolean
  selectable?: boolean
  scrollable?: boolean
  onView?: (a: Attachment) => void
  onToggleSelect?: (id: string, selected: boolean) => void
}) {
  const { t } = useTranslation()
  const attachedCount = selectable
    ? attachments.filter((a) => a.selected).length
    : attachments.length
  return (
    <div className="flex w-full flex-col gap-2.5 border-t border-gray-200 px-2.5 pt-2.5 pb-1">
      <p className="text-xs uppercase text-gray-500">
        {t('editor.attachments.summary', { label, count: attachedCount })}
      </p>
      <div
        className={cn(
          'flex w-full flex-col gap-2.5 py-0.5',
          scrollable && 'max-h-96 overflow-y-auto pr-1',
        )}
      >
        {loading ? (
          <>
            <AttachmentRowSkeleton />
            <AttachmentRowSkeleton />
            <AttachmentRowSkeleton />
          </>
        ) : (
          attachments.map((a) => (
            <AttachmentRow
              key={a.id}
              attachment={a}
              selectable={selectable}
              onView={onView}
              onToggleSelect={onToggleSelect}
            />
          ))
        )}
      </div>
    </div>
  )
}

function FromControl({
  from,
  options,
  onChange,
  selectClassName,
}: {
  from: string
  options: FromOption[]
  onChange?: (value: string) => void
  selectClassName: string
}) {
  if (options.length <= 1) {
    return (
      <span className="text-sm text-gray-900 select-none">
        {options[0]?.label ?? from}
      </span>
    )
  }
  return (
    // Center the trigger as a flex item — the Popover wraps it in an inline-flex
    // that would otherwise sit on the wrapper's text baseline, lifting the value
    // off the row's vertical center while the plain "From" label stays centered.
    <div className="flex w-fit items-center">
      <Select
        value={from}
        onValueChange={onChange}
        options={options}
        className={selectClassName}
        portal
      />
    </div>
  )
}

function FromRow({
  from,
  options,
  onChange,
}: {
  from: string
  options: FromOption[]
  onChange?: (value: string) => void
}) {
  const { t } = useTranslation()
  return (
    <div className="flex h-8 w-full items-center gap-2.5 border-b border-gray-200 px-2.5">
      <span className="text-xs uppercase text-gray-500">
        {t('editor.phone.from')}
      </span>
      <FromControl
        from={from}
        options={options}
        onChange={onChange}
        selectClassName="h-auto border-transparent bg-transparent px-0 shadow-none hover:bg-transparent"
      />
    </div>
  )
}

function RecipientsRow({
  label,
  recipients,
  onRemove,
  onAdd,
  extras,
}: {
  label: string
  recipients: Recipient[]
  onRemove?: (id: string) => void
  onAdd?: (value: string) => void
  extras?: React.ReactNode
}) {
  const { t } = useTranslation()
  return (
    <div className="flex min-h-8 w-full items-center justify-between gap-2.5 border-b border-gray-200 px-2.5 py-1">
      <div className="flex min-w-0 flex-1 items-center gap-2.5">
        <span className="w-8 shrink-0 text-xs uppercase text-gray-500">
          {label}
        </span>
        <RecipientsField
          recipients={recipients}
          onAdd={onAdd}
          onRemove={onRemove}
          placeholder={t('editor.phone.addRecipient')}
          removeAriaLabel={(l) =>
            t('editor.phone.removeRecipient', { label: l })
          }
        />
      </div>
      {extras}
    </div>
  )
}

function CcBccToggle({
  label,
  active,
  onClick,
}: {
  label: string
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        'cursor-pointer transition-colors',
        active ? 'text-primary' : 'text-gray-500 hover:text-gray-900',
      )}
    >
      {label}
    </button>
  )
}

function PillSelect({
  label,
  options,
  onSelect,
  loading,
}: {
  label: string
  options: SelectOption[]
  onSelect?: (value: string) => void
  loading?: boolean
}) {
  return (
    <div className="w-fit">
      <Select
        value=""
        onValueChange={(v) => onSelect?.(v)}
        placeholder={label}
        options={options}
        loading={loading}
        className="h-8 rounded-full bg-gray-100 px-3.75 [&>div>span]:text-gray-900!"
      />
    </div>
  )
}

function InsertFieldOptionRow({
  option,
  onSelect,
}: {
  option: InsertFieldMenuOption
  onSelect?: (option: InsertFieldMenuOption) => void
}) {
  return (
    <DropdownMenuItem onSelect={() => onSelect?.(option)}>
      <span>{option.label}</span>
      {option.suffix && (
        <DropdownMenuShortcut>{option.suffix}</DropdownMenuShortcut>
      )}
    </DropdownMenuItem>
  )
}

function InsertFieldGroupContent({
  group,
  onSelect,
}: {
  group: InsertFieldMenuGroup
  onSelect?: (option: InsertFieldMenuOption) => void
}) {
  return (
    <>
      {group.items.map((item) => (
        <InsertFieldOptionRow key={item.id} option={item} onSelect={onSelect} />
      ))}
      {group.subgroups.map((subgroup) => (
        <DropdownMenuSub key={subgroup.label}>
          <DropdownMenuSubTrigger>{subgroup.label}</DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <InsertFieldGroupContent group={subgroup} onSelect={onSelect} />
          </DropdownMenuSubContent>
        </DropdownMenuSub>
      ))}
    </>
  )
}

function InsertFieldDropdown({
  label,
  groups,
  onSelect,
  triggerClassName,
}: {
  label: string
  groups: InsertFieldMenuGroup[]
  onSelect?: (option: InsertFieldMenuOption) => void
  triggerClassName?: string
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(
            'group flex h-8 cursor-pointer items-center gap-1.25 rounded-full border border-gray-200 bg-gray-100 px-3.75 text-sm text-gray-900 outline-none transition-colors hover:bg-gray-200 focus-visible:focus-ring data-[state=open]:focus-ring',
            triggerClassName,
          )}
        >
          <span>{label}</span>
          <ChevronDownIcon className="size-2.5 transition-transform duration-200 group-data-[state=open]:rotate-180" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-56">
        {groups.map((group) => (
          <DropdownMenuSub key={group.label}>
            <DropdownMenuSubTrigger>{group.label}</DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              <InsertFieldGroupContent group={group} onSelect={onSelect} />
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function useControlledRecipients(
  recipients: Recipient[] | undefined,
  defaultRecipients: Recipient[] | undefined,
  onRemoveRecipient?: (id: string) => void,
  onAddRecipient?: (value: string) => void,
  validate?: (value: string) => boolean,
) {
  const [internal, setInternal] = React.useState<Recipient[]>(
    defaultRecipients ?? [],
  )
  const current = recipients ?? internal
  const remove = (id: string) => {
    if (recipients === undefined)
      setInternal((prev) => prev.filter((r) => r.id !== id))
    onRemoveRecipient?.(id)
  }
  const add = (label: string) => {
    if (validate && !validate(label)) return
    const r: Recipient = { id: `${Date.now()}-${label}`, label }
    if (recipients === undefined) setInternal((prev) => [...prev, r])
    onAddRecipient?.(label)
  }
  return { current, remove, add }
}

function useCcBcc({
  onCcChange,
  onBccChange,
  validate,
}: {
  onCcChange?: (values: string[]) => void
  onBccChange?: (values: string[]) => void
  validate?: (value: string) => boolean
}) {
  const [cc, setCc] = React.useState<Recipient[]>([])
  const [bcc, setBcc] = React.useState<Recipient[]>([])
  const [ccOpen, setCcOpen] = React.useState(false)
  const [bccOpen, setBccOpen] = React.useState(false)
  const showCc = ccOpen || cc.length > 0
  const showBcc = bccOpen || bcc.length > 0

  const ccLabels = cc.map((r) => r.label).join(',')
  const bccLabels = bcc.map((r) => r.label).join(',')
  React.useEffect(() => {
    onCcChange?.(cc.map((r) => r.label))
  }, [ccLabels])
  React.useEffect(() => {
    onBccChange?.(bcc.map((r) => r.label))
  }, [bccLabels])

  const guardedAdd = (
    setter: React.Dispatch<React.SetStateAction<Recipient[]>>,
    label: string,
  ) => {
    if (validate && !validate(label)) return
    setter((p) => [...p, { id: `${Date.now()}-${label}`, label }])
  }

  return {
    cc,
    bcc,
    showCc,
    showBcc,
    addCc: (label: string) => guardedAdd(setCc, label),
    removeCc: (id: string) => setCc((p) => p.filter((r) => r.id !== id)),
    addBcc: (label: string) => guardedAdd(setBcc, label),
    removeBcc: (id: string) => setBcc((p) => p.filter((r) => r.id !== id)),
    toggleCc: () => {
      setCcOpen(!showCc)
      if (showCc) setCc([])
    },
    toggleBcc: () => {
      setBccOpen(!showBcc)
      if (showBcc) setBcc([])
    },
  }
}

function useAttachmentsState(
  controlled: Attachment[] | undefined,
  onUpload: PhoneEditorProps['onUpload'],
  onToggleAttachment: PhoneEditorProps['onToggleAttachment'],
) {
  const [internal, setInternal] = React.useState<Attachment[]>([])
  const current = controlled ?? internal
  const handleFiles = (files: FileList) => {
    onUpload?.(files)
    if (controlled === undefined) {
      const next = Array.from(files).map((f) => ({
        id: `${Date.now()}-${f.name}`,
        name: f.name,
        size: f.size,
        url: URL.createObjectURL(f),
        selected: true,
      }))
      setInternal((prev) => [...prev, ...next])
    }
  }
  const toggle = (id: string, sel: boolean) => {
    if (controlled === undefined)
      setInternal((prev) =>
        prev.map((p) => (p.id === id ? { ...p, selected: sel } : p)),
      )
    onToggleAttachment?.(id, sel)
  }
  return { current, handleFiles, toggle }
}

function UploadButton({
  accept,
  multiple,
  onFiles,
}: {
  accept?: string
  multiple?: boolean
  onFiles: (files: FileList) => void
}) {
  const { t } = useTranslation()
  const ref = React.useRef<HTMLInputElement | null>(null)
  return (
    <>
      <input
        ref={ref}
        type="file"
        accept={accept}
        multiple={multiple}
        className="hidden"
        onChange={(e) => {
          if (e.target.files?.length) onFiles(e.target.files)
          e.target.value = ''
        }}
      />
      <Button
        variant="outline"
        size="xs"
        pressEffect
        onClick={() => ref.current?.click()}
      >
        <AttachIcon className="size-4" />
        {t('editor.phone.uploadFile')}
      </Button>
    </>
  )
}

function CcBccExtras({
  enableCc,
  enableBcc,
  showCc,
  showBcc,
  toggleCc,
  toggleBcc,
}: {
  enableCc: boolean
  enableBcc: boolean
  showCc: boolean
  showBcc: boolean
  toggleCc: () => void
  toggleBcc: () => void
}) {
  const { t } = useTranslation()
  if (!enableCc && !enableBcc) return null
  return (
    <div className="flex shrink-0 items-center gap-2.5 text-sm">
      {enableCc && (
        <CcBccToggle
          label={t('editor.phone.ccToggle')}
          active={showCc}
          onClick={toggleCc}
        />
      )}
      {enableBcc && (
        <CcBccToggle
          label={t('editor.phone.bccToggle')}
          active={showBcc}
          onClick={toggleBcc}
        />
      )}
    </div>
  )
}

function SubjectRow({
  value,
  defaultValue,
  onChange,
  placeholder,
}: {
  value?: string
  defaultValue?: string
  onChange?: (v: string) => void
  placeholder?: string
}) {
  const { t } = useTranslation()
  const [internal, setInternal] = React.useState(defaultValue ?? '')
  const current = value ?? internal
  const handleChange = (v: string) => {
    if (value === undefined) setInternal(v)
    onChange?.(v)
  }
  return (
    <div className="flex h-12 w-full items-center gap-2.5 border-b border-gray-200 bg-gray-100 px-2.5">
      <span className="shrink-0 text-sm text-gray-900">
        {t('editor.email.subject')}
      </span>
      <input
        value={current}
        onChange={(e) => handleChange(e.target.value)}
        placeholder={placeholder}
        className="h-8 flex-1 rounded-md border border-gray-200 bg-white px-2.5 text-sm text-gray-900 placeholder:text-gray-300 focus:border-gray-300 focus:outline-none"
      />
    </div>
  )
}

function InsertFieldPill({
  enabled,
  groups,
  options,
  onOption,
  onValue,
  triggerClassName,
}: {
  enabled: boolean | undefined
  groups: InsertFieldMenuGroup[] | undefined
  options: string[] | undefined
  onOption: ((option: InsertFieldMenuOption) => void) | undefined
  onValue: ((value: string) => void) | undefined
  triggerClassName?: string
}) {
  const { t } = useTranslation()
  if (!enabled) return null
  if (groups && groups.length > 0) {
    return (
      <InsertFieldDropdown
        label={t('editor.phone.insertField')}
        groups={groups}
        onSelect={onOption}
        triggerClassName={triggerClassName}
      />
    )
  }
  if (options) {
    return (
      <PillSelect
        label={t('editor.phone.insertField')}
        options={options.map((o) => ({ value: o, label: o }))}
        onSelect={onValue}
      />
    )
  }
  return null
}

function shouldShowTemplatePill(
  enable: boolean | undefined,
  options: SelectOption[] | undefined,
  loading: boolean | undefined,
) {
  return Boolean(enable) && (Boolean(options) || Boolean(loading))
}

function shouldShowInsertPill(
  enable: boolean | undefined,
  groups: InsertFieldMenuGroup[] | undefined,
  options: string[] | undefined,
) {
  return Boolean(enable) && ((groups?.length ?? 0) > 0 || Boolean(options))
}

function PillsRow({
  enableTemplate,
  enableInsertField,
  templateOptions,
  templateLoading,
  insertFieldOptions,
  insertFieldGroups,
  onTemplateSelect,
  onInsertFieldSelect,
  onInsertFieldOption,
}: Pick<
  PhoneEditorProps,
  | 'enableTemplate'
  | 'enableInsertField'
  | 'templateOptions'
  | 'templateLoading'
  | 'insertFieldOptions'
  | 'insertFieldGroups'
  | 'onTemplateSelect'
  | 'onInsertFieldSelect'
  | 'onInsertFieldOption'
>) {
  const showTemplate = shouldShowTemplatePill(
    enableTemplate,
    templateOptions,
    templateLoading,
  )
  const showInsert = shouldShowInsertPill(
    enableInsertField,
    insertFieldGroups,
    insertFieldOptions,
  )
  if (!showTemplate && !showInsert) return null
  return (
    <div className="flex h-13 w-full items-center gap-2.5 border-b border-gray-200 px-2.5">
      {showTemplate && (
        <div className="min-w-0 max-w-48 flex-1">
          <TemplatePicker
            options={templateOptions ?? []}
            loading={templateLoading}
            onSelect={onTemplateSelect}
          />
        </div>
      )}
      <InsertFieldPill
        enabled={enableInsertField}
        groups={insertFieldGroups}
        options={insertFieldOptions}
        onOption={onInsertFieldOption}
        onValue={onInsertFieldSelect}
      />
    </div>
  )
}

function CollapsibleRow({
  open,
  children,
}: {
  open: boolean
  children: React.ReactNode
}) {
  return (
    <div
      aria-hidden={!open}
      className={cn('collapsible-row', open && 'collapsible-row-open')}
    >
      <div className="overflow-hidden">{children}</div>
    </div>
  )
}

function CcBccRows({
  enableCc,
  enableBcc,
  ccBcc,
}: {
  enableCc: boolean
  enableBcc: boolean
  ccBcc: ReturnType<typeof useCcBcc>
}) {
  const { t } = useTranslation()
  return (
    <>
      {enableCc && (
        <CollapsibleRow open={ccBcc.showCc}>
          <RecipientsRow
            label={t('editor.phone.cc')}
            recipients={ccBcc.cc}
            onRemove={ccBcc.removeCc}
            onAdd={ccBcc.addCc}
          />
        </CollapsibleRow>
      )}
      {enableBcc && (
        <CollapsibleRow open={ccBcc.showBcc}>
          <RecipientsRow
            label={t('editor.phone.bcc')}
            recipients={ccBcc.bcc}
            onRemove={ccBcc.removeBcc}
            onAdd={ccBcc.addBcc}
          />
        </CollapsibleRow>
      )}
    </>
  )
}

function PhoneHeader({
  props,
  fromValue,
  fromOptions,
  setFrom,
  to,
  ccBcc,
}: {
  props: PhoneEditorProps
  fromValue: string
  fromOptions: FromOption[]
  setFrom: (v: string) => void
  to: ReturnType<typeof useControlledRecipients>
  ccBcc: ReturnType<typeof useCcBcc>
}) {
  const { t } = useTranslation()
  const enableFrom = props.enableFrom ?? true
  const enableCc = props.enableCc ?? true
  const enableBcc = props.enableBcc ?? true
  return (
    <>
      {enableFrom && (
        <FromRow from={fromValue} options={fromOptions} onChange={setFrom} />
      )}
      <RecipientsRow
        label={t('editor.phone.to')}
        recipients={to.current}
        onRemove={to.remove}
        onAdd={to.add}
        extras={
          <CcBccExtras
            enableCc={enableCc}
            enableBcc={enableBcc}
            showCc={ccBcc.showCc}
            showBcc={ccBcc.showBcc}
            toggleCc={ccBcc.toggleCc}
            toggleBcc={ccBcc.toggleBcc}
          />
        }
      />
      <CcBccRows enableCc={enableCc} enableBcc={enableBcc} ccBcc={ccBcc} />
      {props.recipientsSummary}
      {props.enableSubject && (
        <SubjectRow
          value={props.subject}
          defaultValue={props.defaultSubject}
          onChange={props.onSubjectChange}
          placeholder={props.subjectPlaceholder}
        />
      )}
      <PillsRow
        enableTemplate={props.enableTemplate ?? true}
        enableInsertField={props.enableInsertField ?? true}
        templateOptions={props.templateOptions}
        templateLoading={props.templateLoading}
        insertFieldOptions={props.insertFieldOptions}
        insertFieldGroups={props.insertFieldGroups}
        onTemplateSelect={props.onTemplateSelect}
        onInsertFieldSelect={props.onInsertFieldSelect}
        onInsertFieldOption={props.onInsertFieldOption}
      />
    </>
  )
}

function toFromOption(option: string | FromOption): FromOption {
  return typeof option === 'string' ? { value: option, label: option } : option
}

function useFromState(
  from: string | undefined,
  fromOptions: (string | FromOption)[] | undefined,
  onFromChange: ((v: string) => void) | undefined,
) {
  const firstOption = fromOptions?.[0]
  const [internal, setInternal] = React.useState(
    from ?? (firstOption === undefined ? '' : toFromOption(firstOption).value),
  )
  const current = from ?? internal
  const options =
    fromOptions?.map(toFromOption) ??
    (current ? [{ value: current, label: current }] : [])
  const setFrom = (v: string) => {
    if (from === undefined) setInternal(v)
    onFromChange?.(v)
  }
  return { current, options, setFrom }
}

function renderBelowContent(
  props: PhoneEditorProps,
  atts: ReturnType<typeof useAttachmentsState>,
  label: string,
) {
  const enable = props.enableAttachments ?? true
  if (!enable) return undefined
  if (atts.current.length === 0 && !props.attachmentsLoading) return undefined
  return (
    <AttachmentsList
      label={label}
      attachments={atts.current}
      loading={props.attachmentsLoading}
      selectable={props.attachmentsSelectable ?? true}
      scrollable={props.attachmentsScrollable}
      onView={props.onViewAttachment}
      onToggleSelect={atts.toggle}
    />
  )
}

/** SMS segment counter fed from the editor body — counts the plain text that is
 *  actually sent. The rich-text body is HTML, so it's flattened first; the
 *  compact plain-text body (no subject) is already plain text and is counted
 *  as-is, so a literal `<` or a newline isn't swallowed by HTML parsing. */
function SmsSegmentFooterItem({
  body,
  plain,
}: {
  body: string
  plain?: boolean
}) {
  const text = React.useMemo(
    // Sanitized like the send path, so the count prices the real wire body.
    () => sanitizeSmsText(plain ? body : htmlToPlainText(body)),
    [body, plain],
  )
  return <SmsSegmentCounter text={text} className="text-xs" />
}

function renderFooterStart(
  props: PhoneEditorProps,
  atts: ReturnType<typeof useAttachmentsState>,
) {
  const upload =
    (props.enableUpload ?? true) ? (
      <UploadButton
        accept={props.uploadAccept}
        multiple={props.uploadMultiple ?? true}
        onFiles={atts.handleFiles}
      />
    ) : null
  const segments = props.showSmsSegments ? (
    <SmsSegmentFooterItem body={props.value ?? ''} />
  ) : null
  if (!upload && !segments) return undefined
  return (
    <>
      {upload}
      {segments}
    </>
  )
}

function CompactDivider() {
  return <div className="h-px w-full bg-gray-200" />
}

function CompactFromTo({
  props,
  fromState,
  to,
  ccBcc,
}: {
  props: PhoneEditorProps
  fromState: ReturnType<typeof useFromState>
  to: ReturnType<typeof useControlledRecipients>
  ccBcc: ReturnType<typeof useCcBcc>
}) {
  const { t } = useTranslation()
  const enableFrom = props.enableFrom ?? true
  const enableCc = props.enableCc ?? true
  const enableBcc = props.enableBcc ?? true
  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
      {enableFrom && (
        <>
          <div className="flex h-9 items-center gap-2 px-3">
            <span className="shrink-0 text-xs text-gray-300">
              {t('editor.phone.from')}
            </span>
            <FromControl
              from={fromState.current}
              options={fromState.options}
              onChange={fromState.setFrom}
              selectClassName="h-auto border-transparent bg-transparent px-0 text-gray-900 shadow-none hover:bg-transparent"
            />
          </div>
          <CompactDivider />
        </>
      )}
      <div className="flex min-h-9 items-center justify-between gap-2 px-3 py-1">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
          <span className="shrink-0 text-xs text-gray-300">
            {t('editor.phone.to')}
          </span>
          <RecipientsField
            recipients={to.current}
            onAdd={to.add}
            onRemove={to.remove}
            placeholder={t('editor.phone.addRecipient')}
            removeAriaLabel={(l) =>
              t('editor.phone.removeRecipient', { label: l })
            }
            className="gap-1.5"
            chipClassName="h-6 bg-gray-100 text-sm text-gray-900"
          />
        </div>
        <CcBccExtras
          enableCc={enableCc}
          enableBcc={enableBcc}
          showCc={ccBcc.showCc}
          showBcc={ccBcc.showBcc}
          toggleCc={ccBcc.toggleCc}
          toggleBcc={ccBcc.toggleBcc}
        />
      </div>
      <CcBccRows enableCc={enableCc} enableBcc={enableBcc} ccBcc={ccBcc} />
    </div>
  )
}

function CompactPills({ props }: { props: PhoneEditorProps }) {
  const showTemplate = shouldShowTemplatePill(
    props.enableTemplate ?? true,
    props.templateOptions,
    props.templateLoading,
  )
  const showInsert = shouldShowInsertPill(
    props.enableInsertField ?? true,
    props.insertFieldGroups,
    props.insertFieldOptions,
  )
  if (!showTemplate && !showInsert) return null
  return (
    <div className="flex items-center gap-2.5">
      {showTemplate && (
        <div className="min-w-0 max-w-48 flex-1">
          <TemplatePicker
            options={props.templateOptions ?? []}
            loading={props.templateLoading}
            onSelect={props.onTemplateSelect}
          />
        </div>
      )}
      <InsertFieldPill
        enabled={props.enableInsertField}
        groups={props.insertFieldGroups}
        options={props.insertFieldOptions}
        onOption={props.onInsertFieldOption}
        onValue={props.onInsertFieldSelect}
        triggerClassName="bg-white hover:bg-gray-50"
      />
    </div>
  )
}

function CompactSubject({ props }: { props: PhoneEditorProps }) {
  const { t } = useTranslation()
  const [internal, setInternal] = React.useState(props.defaultSubject ?? '')
  const current = props.subject ?? internal
  const handle = (v: string) => {
    if (props.subject === undefined) setInternal(v)
    props.onSubjectChange?.(v)
  }
  return (
    <div className="flex items-center gap-2 border-b border-gray-200 px-3 py-1.5">
      <span className="shrink-0 text-xs text-gray-300">
        {t('editor.email.subject')}
      </span>
      <input
        value={current}
        onChange={(e) => handle(e.target.value)}
        placeholder={props.subjectPlaceholder}
        className="min-w-0 flex-1 bg-transparent text-sm text-gray-900 placeholder:text-gray-300 focus:outline-none"
      />
    </div>
  )
}

function CompactFooter({
  props,
  atts,
  canSend,
}: {
  props: PhoneEditorProps
  atts: ReturnType<typeof useAttachmentsState>
  canSend: boolean
}) {
  const { t } = useTranslation()
  return (
    <div className="sticky bottom-0 z-10 flex h-14 items-center justify-between border-t border-gray-200 bg-gray-100 px-2.5">
      <div className="flex items-center gap-2.5">
        {(props.enableUpload ?? true) && (
          <UploadButton
            accept={props.uploadAccept}
            multiple={props.uploadMultiple ?? true}
            onFiles={atts.handleFiles}
          />
        )}
        {props.showSmsSegments && (
          <SmsSegmentFooterItem
            body={props.value ?? ''}
            plain={!props.enableSubject}
          />
        )}
      </div>
      <div className="flex items-center gap-2.5">
        {props.onCancel && (
          <Button
            variant="outline"
            size="xs"
            pressEffect
            onClick={props.onCancel}
          >
            {t('editor.note.cancel')}
          </Button>
        )}
        {props.onSave && (
          <Button
            size="xs"
            pressEffect
            className="px-4"
            disabled={!canSend}
            onClick={() => props.onSave?.(props.value ?? '')}
          >
            <SendIcon className="size-3.5" />
            {props.saveLabel ?? t('editor.email.save')}
          </Button>
        )}
      </div>
    </div>
  )
}

function CompactBody({ props }: { props: PhoneEditorProps }) {
  const { t } = useTranslation()
  const placeholder = props.placeholder ?? t('editor.phone.placeholder')
  if (!props.enableSubject) {
    // Phone/SMS: rich-text surface but no formatting toolbar.
    return (
      <NoteEditor
        className="overflow-hidden rounded-lg border border-gray-200 bg-white"
        value={props.value}
        onChange={props.onChange}
        placeholder={placeholder}
        minHeight={112}
        transformPastedText={sanitizeSmsFragment}
        transformPastedHTML={sanitizeSmsHtml}
        hideToolbar
        enableVoice={props.enableVoice}
        slashGroups={props.slashGroups}
        onSlashInsert={props.onSlashInsert}
        controllerRef={props.controllerRef}
      />
    )
  }
  return (
    <NoteEditor
      className="overflow-hidden rounded-lg border border-gray-200 bg-white"
      header={<CompactSubject props={props} />}
      value={props.value}
      onChange={props.onChange}
      placeholder={placeholder}
      minHeight={112}
      minimalToolbar
      enableTable={props.enableTable}
      enableImage={props.enableImage}
      enableHtmlFidelity={props.enableHtmlFidelity}
      enableVoice={props.enableVoice}
      slashGroups={props.slashGroups}
      onSlashInsert={props.onSlashInsert}
      controllerRef={props.controllerRef}
    />
  )
}

function PhoneEditorCompact({
  props,
  fromState,
  to,
  ccBcc,
  atts,
}: {
  props: PhoneEditorProps
  fromState: ReturnType<typeof useFromState>
  to: ReturnType<typeof useControlledRecipients>
  ccBcc: ReturnType<typeof useCcBcc>
  atts: ReturnType<typeof useAttachmentsState>
}) {
  const { t } = useTranslation()
  // Sending needs both a sender line (when the From row is enabled) and at least
  // one recipient — disable the send button until both numbers are present.
  const hasFrom = (props.enableFrom ?? true) ? Boolean(fromState.current) : true
  const canSend = hasFrom && to.current.length > 0
  const showAttachments =
    (props.enableAttachments ?? true) &&
    (atts.current.length > 0 || Boolean(props.attachmentsLoading))
  return (
    <div className="flex flex-col">
      <div className="flex flex-col gap-3 px-4 py-3">
        <CompactFromTo
          props={props}
          fromState={fromState}
          to={to}
          ccBcc={ccBcc}
        />
        {props.recipientsSummary}
        <CompactPills props={props} />
        <CompactBody props={props} />
      </div>
      {showAttachments && (
        <AttachmentsList
          label={props.attachmentsLabel ?? t('editor.attachments.label')}
          attachments={atts.current}
          loading={props.attachmentsLoading}
          selectable={props.attachmentsSelectable ?? true}
          scrollable={props.attachmentsScrollable}
          onView={props.onViewAttachment}
          onToggleSelect={atts.toggle}
        />
      )}
      <CompactFooter props={props} atts={atts} canSend={canSend} />
    </div>
  )
}

function PhoneEditor(props: PhoneEditorProps) {
  const { t } = useTranslation()
  const fromState = useFromState(
    props.from,
    props.fromOptions,
    props.onFromChange,
  )
  const to = useControlledRecipients(
    props.recipients,
    props.defaultRecipients,
    props.onRemoveRecipient,
    props.onAddRecipient,
    props.validateRecipient,
  )
  const ccBcc = useCcBcc({
    onCcChange: props.onCcChange,
    onBccChange: props.onBccChange,
    validate: props.validateRecipient,
  })
  const atts = useAttachmentsState(
    props.attachments,
    props.onUpload,
    props.onToggleAttachment,
  )

  if (props.compact) {
    return (
      <PhoneEditorCompact
        props={props}
        fromState={fromState}
        to={to}
        ccBcc={ccBcc}
        atts={atts}
      />
    )
  }

  // Phone/SMS mode (no subject) sends as plain text — hide the formatting
  // toolbar, same as the compact body above.
  return (
    <NoteEditor
      value={props.value}
      onChange={props.onChange}
      onCancel={props.onCancel}
      onSave={props.onSave}
      saveLabel={props.saveLabel ?? t('editor.phone.save')}
      placeholder={props.placeholder ?? t('editor.phone.placeholder')}
      transformPastedText={
        props.enableSubject ? undefined : sanitizeSmsFragment
      }
      transformPastedHTML={props.enableSubject ? undefined : sanitizeSmsHtml}
      hideToolbar={!props.enableSubject}
      enableTable={props.enableTable}
      enableImage={props.enableImage}
      enableLink={props.enableLink}
      enableTextAlign={props.enableTextAlign}
      enableSource={props.enableSource}
      enableHtmlFidelity={props.enableHtmlFidelity}
      enableVoice={props.enableVoice}
      slashGroups={props.slashGroups}
      onSlashInsert={props.onSlashInsert}
      controllerRef={props.controllerRef}
      belowContent={renderBelowContent(
        props,
        atts,
        props.attachmentsLabel ?? t('editor.attachments.label'),
      )}
      footerStart={renderFooterStart(props, atts)}
      header={
        <PhoneHeader
          props={props}
          fromValue={fromState.current}
          fromOptions={fromState.options}
          setFrom={fromState.setFrom}
          to={to}
          ccBcc={ccBcc}
        />
      }
    />
  )
}

export { PhoneEditor }
export type {
  Attachment,
  FromOption,
  InsertFieldMenuGroup,
  InsertFieldMenuOption,
  PhoneEditorProps,
  Recipient,
}
